import axios from 'axios';
// Bare origin; append the ingest path here (see App.js). Diagnostics POST here.
const LOGGER_URL = process.env.REACT_APP_TELEMETRY_URL
  ? `${process.env.REACT_APP_TELEMETRY_URL.replace(/\/+$/, '')}/api/frontend-streaming-logs`
  : undefined;

export const getWebRtcDiagnostics = async (appStream, startTime, retryCount = 0) => {
  const baseDiagnostics = { timeSinceStart: Date.now() - startTime };
  try {
    const webRtcController = appStream.stream._webRtcController;
    if (!webRtcController) {
      return { ...baseDiagnostics, error: "WebRtcController not available", stage: "very_early" };
    }

    let pc = webRtcController?.peerConnectionController?.peerConnection;

    if (!pc) {
      try {
        const videoElement = appStream?.stream?.videoElementParent?.querySelector("video");
        if (videoElement?.srcObject) {
          for (const track of videoElement.srcObject.getTracks?.() || []) {
            try {
              const transport = track.receiver?.transport;
              if (transport?.iceTransport) {
                for (const key in transport) {
                  if (transport[key]?.iceConnectionState !== undefined) {
                    pc = transport[key];
                    break;
                  }
                }
              }
            } catch (_) { /* continue */ }
            if (pc) break;
          }
        }
      } catch (_) { /* silent fail */ }
    }

    if (!pc) {
      try {
        const searchForPeerConnection = (obj, depth = 0) => {
          if (depth > 3 || !obj || typeof obj !== 'object') return null;
          if (obj.constructor?.name === 'RTCPeerConnection' ||
            (obj.iceConnectionState !== undefined && obj.connectionState !== undefined)) {
            return obj;
          }
          for (const key in obj) {
            try {
              const found = searchForPeerConnection(obj[key], depth + 1);
              if (found) return found;
            } catch (_) { /* skip inaccessible */ }
          }
          return null;
        };
        pc = searchForPeerConnection(webRtcController);
      } catch (_) { /* silent fail */ }
    }

    if (!pc && retryCount < 3) {
      await new Promise(resolve => setTimeout(resolve, 100 * (retryCount + 1)));
      return getWebRtcDiagnostics(appStream, startTime, retryCount + 1);
    }

    if (!pc) {
      const allProps = [];
      try {
        for (const key in webRtcController) {
          if (key !== 'onCommand') allProps.push(`${key}:${typeof webRtcController[key]}`);
        }
      } catch (_) { allProps.push('Unable to enumerate properties'); }
      return {
        ...baseDiagnostics,
        error: "PeerConnection not available after retries",
        stage: "early",
        webRtcControllerExists: true,
        retryCount,
        availableProperties: allProps.slice(0, 20),
      };
    }

    const diagnostics = {
      ...baseDiagnostics,
      iceConnectionState: pc.iceConnectionState || 'unknown',
      connectionState: pc.connectionState || 'unknown',
      signalingState: pc.signalingState || 'unknown',
      iceGatheringState: pc.iceGatheringState || 'unknown',
      retryCount,
    };

    const pcStats = await pc.getStats().catch(() => null);

    if (pcStats) {
      diagnostics.statsAvailable = true;
      let turnFound = false, hostFound = false, srflxFound = false;
      let connectedPairs = 0, failedPairs = 0;
      let localCandidates = [], remoteCandidates = [], candidatePairs = [];
      let turnServerUrls = [];
      let selectedCandidatePairId = null;

      pcStats.forEach(report => {
        if (report.type === 'local-candidate') {
          const candidateInfo = {
            id: report.id, type: report.candidateType,
            protocol: report.protocol, address: report.address,
            port: report.port, priority: report.priority,
          };
          localCandidates.push(candidateInfo);
          if (report.candidateType === 'relay') { turnFound = true; if (report.address) turnServerUrls.push(report.address); }
          if (report.candidateType === 'host') hostFound = true;
          if (report.candidateType === 'srflx') srflxFound = true;
        }
        if (report.type === 'remote-candidate') {
          remoteCandidates.push({
            id: report.id, type: report.candidateType,
            protocol: report.protocol, address: report.address, port: report.port,
          });
        }
        if (report.type === 'candidate-pair') {
          candidatePairs.push({
            id: report.id, state: report.state,
            localCandidateId: report.localCandidateId,
            remoteCandidateId: report.remoteCandidateId,
            priority: report.priority, nominated: report.nominated,
            bytesSent: report.bytesSent, bytesReceived: report.bytesReceived,
            roundTripTime: report.currentRoundTripTime,
            availableOutgoingBitrate: report.availableOutgoingBitrate,
            availableIncomingBitrate: report.availableIncomingBitrate,
          });
          if (report.state === 'succeeded') connectedPairs++;
          if (report.state === 'failed') failedPairs++;
        }
        if (report.type === 'transport') {
          diagnostics.transport = {
            dtlsState: report.dtlsState,
            selectedCandidatePairId: report.selectedCandidatePairId,
            bytesReceived: report.bytesReceived, bytesSent: report.bytesSent,
            packetsReceived: report.packetsReceived, packetsSent: report.packetsSent,
          };
          selectedCandidatePairId = report.selectedCandidatePairId;
        }
        if (report.type === 'inbound-rtp') {
          diagnostics.inboundRtp = {
            ssrc: report.ssrc, mediaType: report.mediaType, codecId: report.codecId,
            packetsReceived: report.packetsReceived, packetsLost: report.packetsLost,
            bytesReceived: report.bytesReceived, jitter: report.jitter,
            framesDecoded: report.framesDecoded, framesDropped: report.framesDropped,
          };
        }
        if (report.type === 'outbound-rtp') {
          diagnostics.outboundRtp = {
            ssrc: report.ssrc, mediaType: report.mediaType, codecId: report.codecId,
            packetsSent: report.packetsSent, bytesSent: report.bytesSent,
            framesEncoded: report.framesEncoded,
          };
        }
      });

      if (selectedCandidatePairId) {
        const selectedPair = candidatePairs.find(p => p.id === selectedCandidatePairId);
        if (selectedPair) {
          const selectedLocal = localCandidates.find(c => c.id === selectedPair.localCandidateId);
          if (selectedLocal) {
            if (selectedLocal.type === 'relay') {
              const relayInfo = {
                address: selectedLocal.address,
                port: selectedLocal.port,
                protocol: selectedLocal.protocol,
              };
              let turnServerInfo = null;
              try {
                const pcConfig = pc.getConfiguration();
                if (pcConfig?.iceServers) {
                  outer: for (const iceServer of pcConfig.iceServers) {
                    const urls = Array.isArray(iceServer.urls) ? iceServer.urls : [iceServer.urls];
                    for (const url of urls) {
                      if (!url || typeof url !== 'string') continue;
                      const turnMatch = url.match(/turn[s]?:(?:\/\/)?([^:?]+):?(\d+)?/i);
                      if (!turnMatch) continue;
                      const turnHost = turnMatch[1];
                      const turnPort = turnMatch[2] || (url.includes('turns:') ? '5349' : '3478');
                      const transportMatch = url.match(/[?&]transport=([^&]+)/i);
                      const urlTransport = transportMatch ? transportMatch[1].toLowerCase() : 'udp';
                      if (selectedLocal.address === turnHost && selectedLocal.protocol === urlTransport) {
                        turnServerInfo = {
                          url, host: turnHost,
                          port: parseInt(turnPort, 10), transport: urlTransport,
                          secure: url.startsWith('turns:'),
                          username: iceServer.username || null,
                          credentialType: iceServer.credential ? 'password' : null,
                        };
                        break outer;
                      }
                    }
                  }
                  if (!turnServerInfo) {
                    const allTurnServers = [];
                    for (const iceServer of pcConfig.iceServers) {
                      const urls = Array.isArray(iceServer.urls) ? iceServer.urls : [iceServer.urls];
                      for (const url of urls) {
                        if (url && typeof url === 'string' && /^turn[s]?:/i.test(url)) {
                          const m = url.match(/turn[s]?:(?:\/\/)?([^:?]+):?(\d+)?/i);
                          if (m) allTurnServers.push({ url, host: m[1], port: m[2] || (url.includes('turns:') ? '5349' : '3478') });
                        }
                      }
                    }
                    diagnostics.availableTurnServers = allTurnServers;
                  }
                }
              } catch (_) { /* silent fail */ }
              diagnostics.selectedTurn = { relay: relayInfo, server: turnServerInfo };
              diagnostics.connectionType = 'TURN';
              diagnostics.selectedServer = turnServerInfo
                ? `${turnServerInfo.url} → relay:${relayInfo.address}:${relayInfo.port}`
                : `relay:${relayInfo.address}:${relayInfo.port} (server URL unknown)`;
            } else {
              diagnostics.selectedConnection = {
                type: selectedLocal.type, address: selectedLocal.address,
                port: selectedLocal.port, protocol: selectedLocal.protocol,
              };
              diagnostics.connectionType = selectedLocal.type.toUpperCase();
              diagnostics.selectedServer = `${selectedLocal.address}:${selectedLocal.port}`;
            }
          }
        }
      }

      diagnostics.turnCandidatesFound = turnFound;
      diagnostics.hostCandidatesFound = hostFound;
      diagnostics.srflxCandidatesFound = srflxFound;
      diagnostics.candidatePairs = candidatePairs.length;
      diagnostics.connectedPairs = connectedPairs;
      diagnostics.failedPairs = failedPairs;
      diagnostics.localCandidates = localCandidates;
      diagnostics.remoteCandidates = remoteCandidates;
      diagnostics.candidatePairsDetails = candidatePairs;
      diagnostics.turnServerUrls = turnServerUrls;
    } else {
      diagnostics.statsAvailable = false;
      diagnostics.note = "Stats not available yet — connection may be in early stage";
      diagnostics.turnCandidatesFound = false;
      diagnostics.hostCandidatesFound = false;
      diagnostics.srflxCandidatesFound = false;
      diagnostics.candidatePairs = 0;
      diagnostics.connectedPairs = 0;
      diagnostics.failedPairs = 0;
    }

    const issues = [];
    if (pc.iceConnectionState === 'failed') issues.push('ICE_FAILED');
    if (pc.iceConnectionState === 'disconnected') issues.push('ICE_DISCONNECTED');
    if (pc.connectionState === 'failed') issues.push('PEER_CONNECTION_FAILED');

    if (diagnostics.statsAvailable) {
      const isEarlyStage = pc.iceGatheringState === 'new' || pc.iceConnectionState === 'new' ||
        (pc.iceGatheringState === 'gathering' && diagnostics.timeSinceStart < 5000);
      const isCheckingOrFailed = pc.iceConnectionState === 'checking' ||
        pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected';
      const isGatheringComplete = pc.iceGatheringState === 'complete';

      if (!diagnostics.turnCandidatesFound && !isEarlyStage &&
        (isCheckingOrFailed || isGatheringComplete) &&
        pc.iceConnectionState !== 'connected' && pc.iceConnectionState !== 'completed') {
        issues.push('NO_TURN');
      }
      if (!diagnostics.hostCandidatesFound && !diagnostics.srflxCandidatesFound &&
        !diagnostics.turnCandidatesFound && (isGatheringComplete || isCheckingOrFailed) && !isEarlyStage) {
        issues.push('NO_CANDIDATES');
      }
      if (diagnostics.failedPairs > 0 && diagnostics.connectedPairs === 0 && diagnostics.candidatePairs > 0) {
        issues.push('ALL_CANDIDATE_PAIRS_FAILED');
      }
    }

    if (pc.iceConnectionState === 'checking' && diagnostics.timeSinceStart > 30000) {
      issues.push('ICE_CHECKING_TIMEOUT');
    }

    if (issues.length > 0) {
      diagnostics.issues = issues;
      if (issues.includes('NO_TURN') || issues.includes('ICE_FAILED') || issues.includes('ALL_CANDIDATE_PAIRS_FAILED')) {
        diagnostics.likelyCause = 'TURN_SERVER_ISSUE';
      } else if (issues.includes('ICE_CHECKING_TIMEOUT')) {
        diagnostics.likelyCause = 'NETWORK_FIREWALL_ISSUE';
      } else if (issues.includes('NO_CANDIDATES')) {
        diagnostics.likelyCause = 'SIGNALLING_SERVER_ISSUE';
      } else {
        diagnostics.likelyCause = 'UNKNOWN_ISSUE';
      }
    }

    return diagnostics;
  } catch (e) {
    return { error: e.message, errorStack: e.stack };
  }
};

// Embed attribution: when the share page is IFRAMED on a customer's site, its
// own location is OUR share URL, so the customer's domain never reached the
// session telemetry (their page showed up as "share.streampixel.io"). The
// WebSDK never has this problem — it runs directly on the customer's page.
// Cross-origin rules cap what an iframe can know about its parent:
//   * the TOP page's ORIGIN is readable (ancestorOrigins; Firefox lacks it, so
//     document.referrer — which the default strict-origin-when-cross-origin
//     policy trims to the origin anyway);
//   * the parent's full PATH (/tower) is NOT, unless the embedding page opts
//     in with a laxer referrerpolicy on the iframe — when it does, referrer
//     carries the path and we report it.
// Computed once: the embedder cannot change for the life of the page.
const embedContext = (() => {
  try {
    if (window.self === window.top) return { embedded: false };
    const chain = window.location.ancestorOrigins;
    const topOrigin = chain && chain.length ? chain[chain.length - 1] : null;
    const referrer = document.referrer || null;
    return { embedded: true, embedderOrigin: topOrigin || referrer, embedderReferrer: referrer };
  } catch (_) {
    return { embedded: true, embedderOrigin: null };
  }
})();

export const logWebRtcDiagnostics = (event, telemetryToken,diagnostics = {}, config) => {
  const loggerPostOpts = () => ({
    timeout: 1500,
    headers: telemetryToken ? { Authorization: `Bearer ${telemetryToken}` } : {},
  });

  const { streamerId, projectId, projectRegion, startTime } = config;
  const data = {
    logType: "Webrtc",
    streamerId, projectId, projectRegion,
    event,
    timestamp: new Date().toISOString(),
    timeSinceStart: Date.now() - startTime,
    ...embedContext,
    ...diagnostics,
  };
  axios.post(LOGGER_URL, data, loggerPostOpts())
    .catch(err => { if (err.code !== 'ECONNABORTED') console.warn('WebRTC log failed:', err.message); });
};

export const findPcForTcpCheck = (appStream) => {
  const wrc = appStream?.stream?._webRtcController;
  if (!wrc) return null;

  const pc = wrc?.peerConnectionController?.peerConnection;
  if (pc) return pc;
  const search = (obj, depth = 0) => {
    if (depth > 3 || !obj || typeof obj !== 'object') return null;
    if (obj.iceConnectionState !== undefined && obj.connectionState !== undefined) return obj;
    for (const k in obj) {
      try { const f = search(obj[k], depth + 1); if (f) return f; } catch (_) {}
    }
    return null;
  };
  try { return search(wrc); } catch (_) { return null; }
};

/**
 * WHICH PATH — and, when relayed, WHOSE RELAY — carried this session.
 *
 * The TURN bill can only be attributed per-session from here: the server sees
 * allocations from every listed provider on every connect (the browser
 * allocates on all of them), so allocation logs cannot say who actually moved
 * the media. Only the SELECTED candidate pair knows, and only the client can
 * read it. Logged once on `connected`, this is what turns "Twilio charged us
 * $X" into "these sessions, on these projects, relayed through provider Y" —
 * the before/after evidence for the coturn migration.
 *
 * localPath: 'host' | 'srflx' | 'relay' | 'unknown' — this browser's side.
 * remotePath: same for the streamer's side (its selected candidate as we see
 *   it), which is how streamer-side TURN use is measured without touching UE.
 * relayUrl/relayAddress: the TURN url the browser used (Chrome reports it on
 *   the local candidate) and the relay's IP — either one attributes the
 *   provider; both are logged because neither is guaranteed on every browser.
 *
 * Never throws: a diagnostics reader that can break `connected` logging would
 * cost more than it measures. Absent stats answer 'unknown', not an error.
 */
export const getSelectedPathInfo = async (appStream) => {
  const info = { localPath: 'unknown', remotePath: 'unknown', relayProtocol: null, relayUrl: null, relayAddress: null };
  try {
    const pc = findPcForTcpCheck(appStream);
    if (!pc) return info;
    const stats = await pc.getStats().catch(() => null);
    if (!stats) return info;
    const locals = new Map(); const remotes = new Map();
    let pair = null, selectedId = null;
    stats.forEach(r => {
      if (r.type === 'local-candidate') locals.set(r.id, r);
      else if (r.type === 'remote-candidate') remotes.set(r.id, r);
      else if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded') pair = r;
      else if (r.type === 'transport' && r.selectedCandidatePairId) selectedId = r.selectedCandidatePairId;
    });
    if (!pair && selectedId) stats.forEach(r => { if (r.id === selectedId) pair = r; });
    if (!pair) return info;
    const local = locals.get(pair.localCandidateId);
    const remote = remotes.get(pair.remoteCandidateId);
    if (local) {
      info.localPath = local.candidateType || 'unknown';
      if (local.candidateType === 'relay') {
        info.relayProtocol = (local.relayProtocol || local.protocol || '').toLowerCase() || null;
        info.relayUrl = local.url || null;
        info.relayAddress = local.address || local.ip || null;
      }
    }
    if (remote) info.remotePath = remote.candidateType || 'unknown';
    return info;
  } catch (_) {
    return info;
  }
};

export const isTcpRelayActive = async (appStream) => {
  const pc = findPcForTcpCheck(appStream);
  if (!pc) return false;
  const stats = await pc.getStats().catch(() => null);
  if (!stats) return false;
  const locals = new Map();
  let pair = null, selectedId = null;
  stats.forEach(r => {
    if (r.type === 'local-candidate') locals.set(r.id, r);
    else if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded') pair = r;
    else if (r.type === 'transport' && r.selectedCandidatePairId) selectedId = r.selectedCandidatePairId;
  });
  if (!pair && selectedId) stats.forEach(r => { if (r.id === selectedId) pair = r; });
  const local = pair && locals.get(pair.localCandidateId);
  if (!local || local.candidateType !== 'relay') return false;
  const url = (local.url || '').toLowerCase();
  return (local.relayProtocol || '').toLowerCase() === 'tcp'
    || (local.protocol || '').toLowerCase() === 'tcp'
    || url.includes('transport=tcp')
    || url.startsWith('turns:');
};
